import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import NestedJSONGrid from './NestedJSONGrid';
import { ArrowLeft, Save, Download } from 'lucide-react';
import Papa from 'papaparse';

const NestedJSONViewerPage = ({ user, data: routeData, headers: routeHeaders, onBack }) => {
    const [data, setData] = useState(routeData || []);

    if (!routeData || routeData.length === 0) {
        return (
            <div className="flex flex-col h-screen bg-gray-50">
                <Header title="DataCreator AI" user={user} />
                <div className="flex-1 flex flex-col items-center justify-center p-8">
                    <h2 className="text-xl font-semibold mb-4">No Data Found</h2>
                    <button
                        onClick={onBack}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                        Go back
                    </button>
                </div>
            </div>
        );
    }

    const handleDownload = () => {
        // Re-construct nested objects from flat headers if data came in flat
        // This simulates the logic from NestedJSONGrid internally, but for downloading
        const nestedData = data.map(row => {
            const nested = {};
            Object.keys(row).filter(k => k !== '_rowId').forEach(key => {
                const parts = key.split('.');
                let current = nested;
                for (let i = 0; i < parts.length - 1; i++) {
                    const part = parts[i];
                    if (!current[part]) current[part] = {};
                    current = current[part];
                }
                let val = row[key];
                if (typeof val === 'string' && (val.trim().startsWith('[') || val.trim().startsWith('{'))) {
                    try { val = JSON.parse(val); } catch (e) { }
                }
                current[parts[parts.length - 1]] = val;
            });
            return nested;
        });

        const blob = new Blob([JSON.stringify(nestedData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'nested-data.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
            <Header title="DataCreator AI" user={user} />

            <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shadow-sm z-10">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2 font-medium"
                    >
                        <ArrowLeft size={18} />
                        Back
                    </button>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            Nested JSON Viewer
                        </h1>
                        <p className="text-xs text-gray-500">Read-only view of nested data structures</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleDownload}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 transition-colors shadow-sm text-sm font-medium"
                    >
                        <Download size={16} />
                        Download JSON
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-hidden relative p-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full overflow-hidden flex flex-col">
                    <NestedJSONGrid
                        data={data}
                        headers={routeHeaders || []}
                        currentPage={1}
                        rowsPerPage={data.length} // Just unroll all for view/scroll or implement internal paginator
                        previewMode={true} // Read-only mode
                    />
                </div>
            </div>
        </div>
    );
};

export default NestedJSONViewerPage;
